// Typed helpers for the Phase 1 feed HTTP contract (docs/design/phase1-contracts.md §3).
// URL builders are pure so they can be unit-tested; every path is under /api.
import type {
  ActionRequest,
  ActionResult,
  ApprovalPreview,
  AuditEventView,
  BoardView,
  CardSummary,
  CardView,
  DocumentSummary,
  DocumentView,
  DraftRequestView,
  FeedConfigView,
  FeedSummary,
  FocusPositionView,
  HiddenThreadView,
  InstructionRequest,
  ProposalView,
  ResearchQueuedView,
  RoutineActionResult,
  RoutineGroupView,
  ThreadView,
  WorkView,
} from "@shared/api";
import type { ActionKind } from "@shared/core";
import type { RoundOrder } from "@shared/startEasily";
import { api } from "./api";

const enc = encodeURIComponent;

function query(params: Record<string, string | number | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

/** Recovery routes for a work item (WorkView.recovery, minus "edit" which has its own route). */
export type WorkRecovery = "cancel" | "retry" | "recover" | "reconcile" | "discard";

export interface AuditParams {
  feedId?: string | undefined;
  cardId?: string | undefined;
  limit?: number | undefined;
  before?: string | undefined;
}

export interface HiddenParams {
  limit?: number | undefined;
  before?: string | undefined;
}

export const feedUrls = {
  feeds: () => "/api/feeds",
  feed: (feedId: string) => `/api/feeds/${enc(feedId)}`,
  board: (feedId: string) => `/api/feeds/${enc(feedId)}/board`,
  snoozed: (feedId: string) => `/api/feeds/${enc(feedId)}/snoozed`,
  config: (feedId: string) => `/api/feeds/${enc(feedId)}/config`,
  instructions: (feedId: string) => `/api/feeds/${enc(feedId)}/instructions`,
  nextPass: (feedId: string) => `/api/feeds/${enc(feedId)}/next-pass`,
  focusPosition: (feedId: string) => `/api/feeds/${enc(feedId)}/focus-position`,
  recollect: (feedId: string) => `/api/feeds/${enc(feedId)}/recollect`,
  learning: (feedId: string) => `/api/feeds/${enc(feedId)}/learning`,
  work: (feedId: string, statuses?: readonly string[]) =>
    `/api/feeds/${enc(feedId)}/work${query({ status: statuses?.length ? statuses.join(",") : undefined })}`,
  card: (cardId: string) => `/api/cards/${enc(cardId)}`,
  cardThread: (cardId: string, images: boolean) =>
    `/api/cards/${enc(cardId)}/thread${query({ images: images ? 1 : 0 })}`,
  block: (cardId: string, blockId: string) => `/api/cards/${enc(cardId)}/blocks/${enc(blockId)}`,
  action: (cardId: string, actionId: string) =>
    `/api/cards/${enc(cardId)}/actions/${enc(actionId)}`,
  preview: (cardId: string, actionId: string) =>
    `/api/cards/${enc(cardId)}/actions/${enc(actionId)}/preview`,
  dismiss: (cardId: string) => `/api/cards/${enc(cardId)}/dismiss`,
  restore: (cardId: string) => `/api/cards/${enc(cardId)}/restore`,
  snooze: (cardId: string) => `/api/cards/${enc(cardId)}/snooze`,
  research: (cardId: string) => `/api/cards/${enc(cardId)}/research`,
  draftAhead: (cardId: string) => `/api/cards/${enc(cardId)}/draft-ahead`,
  draft: (cardId: string) => `/api/cards/${enc(cardId)}/draft`,
  workAction: (workId: string, action: WorkRecovery) => `/api/work/${enc(workId)}/${action}`,
  workInstruction: (workId: string) => `/api/work/${enc(workId)}/instruction`,
  routineGroup: (id: string) => `/api/routine-groups/${enc(id)}`,
  routineApprove: (id: string) => `/api/routine-groups/${enc(id)}/approve`,
  docs: () => "/api/docs",
  /** Doc keys contain slashes; they travel as ONE encoded path segment. */
  doc: (key: string) => `/api/docs/${enc(key)}`,
  docRevert: (key: string, revisionId: string) => `/api/docs/${enc(key)}/revert/${enc(revisionId)}`,
  docRestore: (key: string, revisionId: string) =>
    `/api/docs/${enc(key)}/restore/${enc(revisionId)}`,
  proposals: (status?: string) => `/api/proposals${query({ status })}`,
  proposalApply: (id: string) => `/api/proposals/${enc(id)}/apply`,
  proposalReject: (id: string) => `/api/proposals/${enc(id)}/reject`,
  audit: (params: AuditParams) =>
    `/api/audit${query({
      feedId: params.feedId,
      cardId: params.cardId,
      limit: params.limit,
      before: params.before,
    })}`,
  hidden: (params: HiddenParams) =>
    `/api/hidden${query({ limit: params.limit, before: params.before })}`,
  hiddenRestore: (id: string) => `/api/hidden/${enc(id)}/restore`,
};

export interface FeedUpdate {
  name?: string;
  purpose?: string;
  defaultCleanupKind?: ActionKind | null;
}

export const feedApi = {
  feeds: (signal?: AbortSignal) => api<FeedSummary[]>(feedUrls.feeds(), { signal }),
  updateFeed: (feedId: string, body: FeedUpdate) =>
    api<FeedSummary>(feedUrls.feed(feedId), { method: "PUT", body }),
  board: (feedId: string, signal?: AbortSignal) =>
    api<BoardView>(feedUrls.board(feedId), { signal }),
  snoozed: (feedId: string, signal?: AbortSignal) =>
    api<CardSummary[]>(feedUrls.snoozed(feedId), { signal }),
  config: (feedId: string, signal?: AbortSignal) =>
    api<FeedConfigView>(feedUrls.config(feedId), { signal }),
  instruct: (feedId: string, body: InstructionRequest) =>
    api<WorkView>(feedUrls.instructions(feedId), { method: "POST", body }),
  /** Starts the next round; `order` is chosen once, here (U3 "Just the quick ones"). */
  nextPass: (feedId: string, order?: RoundOrder) =>
    api<BoardView>(feedUrls.nextPass(feedId), {
      method: "POST",
      ...(order ? { body: { order } } : {}),
    }),
  focusPosition: (feedId: string, signal?: AbortSignal) =>
    api<FocusPositionView>(feedUrls.focusPosition(feedId), { signal }),
  saveFocusPosition: (
    feedId: string,
    body: { passNo: number; index: number; total: number; stepId: string | null },
  ) => api<FocusPositionView>(feedUrls.focusPosition(feedId), { method: "PUT", body }),
  /** Bound to the round: a stale tab's clear never touches a newer round's position. */
  clearFocusPosition: (feedId: string, passNo: number) =>
    api<FocusPositionView>(`${feedUrls.focusPosition(feedId)}${query({ passNo })}`, {
      method: "DELETE",
    }),
  recollect: (feedId: string) => api<WorkView>(feedUrls.recollect(feedId), { method: "POST" }),
  learning: (feedId: string, accept: boolean) =>
    api<{ work: WorkView | null }>(feedUrls.learning(feedId), {
      method: "POST",
      body: { accept },
    }),
  work: (feedId: string, statuses?: readonly string[], signal?: AbortSignal) =>
    api<WorkView[]>(feedUrls.work(feedId, statuses), { signal }),

  card: (cardId: string, signal?: AbortSignal) => api<CardView>(feedUrls.card(cardId), { signal }),
  cardThread: (cardId: string, images: boolean, signal?: AbortSignal) =>
    api<ThreadView>(feedUrls.cardThread(cardId, images), { signal }),
  saveBlock: (cardId: string, blockId: string, value: string, cardRev: number) =>
    api<CardView>(feedUrls.block(cardId, blockId), { method: "POST", body: { value, cardRev } }),
  action: (cardId: string, actionId: string, body: ActionRequest) =>
    api<ActionResult>(feedUrls.action(cardId, actionId), { method: "POST", body }),
  /** What the gateway would bind if this action were approved now (computed on the server). */
  preview: (cardId: string, actionId: string, signal?: AbortSignal) =>
    api<ApprovalPreview>(feedUrls.preview(cardId, actionId), { signal }),
  dismiss: (cardId: string) => api<CardView>(feedUrls.dismiss(cardId), { method: "POST" }),
  restore: (cardId: string) => api<CardView>(feedUrls.restore(cardId), { method: "POST" }),
  snooze: (cardId: string, until: string) =>
    api<CardView>(feedUrls.snooze(cardId), { method: "POST", body: { until } }),
  /** `r`: research what the card is about (the same research work the operator can queue). */
  research: (cardId: string) =>
    api<ResearchQueuedView>(feedUrls.research(cardId), { method: "POST" }),
  /** U4: a draft written ahead as the owner approaches a card that needs a reply. */
  draftAhead: (cardId: string) =>
    api<DraftRequestView>(feedUrls.draftAhead(cardId), { method: "POST" }),
  /** U4: the owner asks for a draft (`e` without one, or an answer to the card's question). */
  draft: (cardId: string, answer?: string) =>
    api<DraftRequestView>(feedUrls.draft(cardId), {
      method: "POST",
      body: answer ? { answer } : {},
    }),

  workAction: (workId: string, action: WorkRecovery) =>
    api<WorkView>(feedUrls.workAction(workId, action), { method: "POST" }),
  editWork: (workId: string, instruction: string) =>
    api<WorkView>(feedUrls.workInstruction(workId), { method: "POST", body: { instruction } }),

  routineGroup: (id: string, signal?: AbortSignal) =>
    api<RoutineGroupView>(feedUrls.routineGroup(id), { signal }),
  approveRoutine: (id: string, itemThreadKeys: string[], itemsRev: string) =>
    api<RoutineActionResult>(feedUrls.routineApprove(id), {
      method: "POST",
      body: { itemThreadKeys, itemsRev },
    }),

  docs: (signal?: AbortSignal) => api<DocumentSummary[]>(feedUrls.docs(), { signal }),
  doc: (key: string, signal?: AbortSignal) => api<DocumentView>(feedUrls.doc(key), { signal }),
  saveDoc: (key: string, content: string, baseRev: number) =>
    api<DocumentView>(feedUrls.doc(key), { method: "PUT", body: { content, baseRev } }),
  revertDoc: (key: string, revisionId: string) =>
    api<DocumentView>(feedUrls.docRevert(key, revisionId), { method: "POST" }),
  restoreDoc: (key: string, revisionId: string, baseRev: number) =>
    api<DocumentView>(feedUrls.docRestore(key, revisionId), { method: "POST", body: { baseRev } }),

  proposals: (status: string | undefined, signal?: AbortSignal) =>
    api<ProposalView[]>(feedUrls.proposals(status), { signal }),
  applyProposal: (id: string, next?: string) =>
    api<ProposalView>(feedUrls.proposalApply(id), {
      method: "POST",
      body: next === undefined ? {} : { next },
    }),
  rejectProposal: (id: string) =>
    api<ProposalView>(feedUrls.proposalReject(id), { method: "POST" }),

  audit: (params: AuditParams, signal?: AbortSignal) =>
    api<AuditEventView[]>(feedUrls.audit(params), { signal }),
  hidden: (params: HiddenParams, signal?: AbortSignal) =>
    api<HiddenThreadView[]>(feedUrls.hidden(params), { signal }),
  restoreHidden: (id: string) =>
    api<HiddenThreadView>(feedUrls.hiddenRestore(id), { method: "POST" }),
};
